import { useEffect, useState } from "react";

const STEPS = [
  { k: "01", t: "Identify Gaps",   d: "CBPs and district asks signal precise competency shortages.", c: "#D95D39" },
  { k: "02", t: "Design",          d: "Institutions translate demand into targeted, competency-linked programmes.", c: "#D95D39" },
  { k: "03", t: "Deliver",         d: "Structured programmes reach officials across the network.", c: "#0077B6" },
  { k: "04", t: "Capture Data",    d: "Every touchpoint silently feeds participation & learning outcomes.", c: "#0077B6" },
  { k: "05", t: "Generate Insights", d: "Data refines the next CBP, the next programme, the next plan.", c: "#4C956C" },
];

export default function ContinuousLoop() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive(a => (a + 1) % STEPS.length), 2400);
    return () => clearInterval(t);
  }, []);

  const R = 180;
  const CENTER = 240;

  return (
    <section id="loop" data-testid="continuous-loop-section" className="bg-navy text-cream py-28 md:py-36 dark-section relative overflow-hidden">
      <div className="absolute inset-0 bg-lattice-dark opacity-40" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="font-mono-data text-xs tracking-[0.3em] uppercase text-[#E8B298] mb-4">02 · Continuous Learning Engine</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl leading-[1.05]">
            A system that <span className="italic text-[#E8B298]">improves itself.</span>
          </h2>
          <p className="mt-6 text-[15px] leading-relaxed text-cream/75">
            No manual data entry. No orphaned feedback. Every programme delivered feeds the next one — automatically
            captured, competency-mapped, and surfaced back into planning.
          </p>
          <ol className="mt-10 space-y-3">
            {STEPS.map((s, i) => (
              <li key={s.k} data-testid={`loop-step-${i}`}
                  className={`flex items-start gap-4 border-l-2 pl-5 py-2 transition-all ${
                    active === i ? "border-[#D95D39] bg-white/5" : "border-white/10"
                  }`}>
                <span className="font-mono-data text-sm text-[#E8B298] pt-1">{s.k}</span>
                <div>
                  <div className="font-serif-display text-xl">{s.t}</div>
                  <div className="text-sm text-cream/65">{s.d}</div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="lg:col-span-7 flex justify-center">
          <svg viewBox="0 0 480 480" className="w-full max-w-[520px]" data-testid="loop-diagram">
            <circle cx={CENTER} cy={CENTER} r={R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
            <circle cx={CENTER} cy={CENTER} r={R - 40} fill="none" stroke="rgba(255,255,255,0.08)" strokeDasharray="2 6" />
            <circle cx={CENTER} cy={CENTER} r={R + 40} fill="none" stroke="rgba(255,255,255,0.08)" strokeDasharray="2 6" />
            <circle cx={CENTER} cy={CENTER} r="10" fill="#D95D39" className="pulse-node" />
            <text x={CENTER} y={CENTER + 40} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10" letterSpacing="3" fill="#E8B298">LIVE</text>

            {STEPS.map((s, i) => {
              const angle = (i / STEPS.length) * Math.PI * 2 - Math.PI / 2;
              const x = CENTER + R * Math.cos(angle);
              const y = CENTER + R * Math.sin(angle);
              const isActive = active === i;
              return (
                <g key={s.k}>
                  <circle cx={x} cy={y} r={isActive ? 26 : 18} fill={s.c} opacity={isActive ? 0.22 : 0.14} />
                  <circle cx={x} cy={y} r="12" fill={s.c} />
                  <text x={x} y={y + 4} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="11" fill="#fff" fontWeight="600">{s.k}</text>
                  <text x={x} y={y - 26} textAnchor="middle" fontFamily="Fraunces" fontSize="14" fill="#F8F9FA">{s.t}</text>
                </g>
              );
            })}

            {STEPS.map((s, i) => {
              const a1 = (i / STEPS.length) * Math.PI * 2 - Math.PI / 2;
              const a2 = ((i + 1) / STEPS.length) * Math.PI * 2 - Math.PI / 2;
              const x1 = CENTER + R * Math.cos(a1);
              const y1 = CENTER + R * Math.sin(a1);
              const x2 = CENTER + R * Math.cos(a2);
              const y2 = CENTER + R * Math.sin(a2);
              return <path key={i} d={`M ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`}
                           fill="none" stroke={active === i ? "#D95D39" : "rgba(255,255,255,0.2)"} strokeWidth={active === i ? "3" : "1"} />;
            })}
          </svg>
        </div>
      </div>
    </section>
  );
}
